import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as A from '../api/planning.ts';
import { correlationId, invalidatePlanning, keys } from '../lib/mutate.ts';
import { hours } from '../lib/dates.ts';
import { Avatar, Banner, Spinner } from '../ui/bits.tsx';

/**
 * Name a werf, from the screen that lists them.
 *
 * WHY THIS IS HERE AND NOT IN A SEED SCRIPT: the name is the label every other screen shows large, and
 * until now it could only be set by someone with a database session. "Deze werf heet Baudelo Park" is
 * something the planner standing on it knows and nobody else does — asking an administrator for it is
 * how a werf stays called BS-650 forever.
 */
function WerfName({ p }: { p: A.PlannerProject }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(p.projectName ?? '');
  const [error, setError] = useState<string | null>(null);
  const label = A.werfLabel(p);

  const save = async () => {
    try {
      await A.renameProject(p.projectId, value.trim() || null, correlationId());
      qc.invalidateQueries({ queryKey: keys.projects });
      invalidatePlanning(qc, p.projectId);
      setEditing(false); setError(null);
    } catch (e) { setError((e as Error).message); }
  };

  if (!editing) {
    return (
      <span className="stack" style={{ gap: 2, minWidth: 0 }}>
        <span className="row" style={{ gap: 6 }}>
          <strong className="werf-name" style={{ fontSize: 16 }}>{label.name}</strong>
          <button className="btn small" title="Werf hernoemen"
                  onClick={(e) => { e.stopPropagation(); setValue(p.projectName ?? ''); setEditing(true); }}>✎</button>
        </span>
        {label.code && <span className="werf-code">{label.code}</span>}
        <span className="faint">{p.customerName ?? 'geen klant gekoppeld'}</span>
      </span>
    );
  }
  return (
    <span className="stack" style={{ gap: 4, minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
      <input value={value} maxLength={200} autoFocus placeholder={p.projectCode}
             onChange={(e) => setValue(e.target.value)}
             onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }} />
      <span className="row" style={{ gap: 6 }}>
        <button className="btn small primary" onClick={save}>Bewaren</button>
        <button className="btn small" onClick={() => setEditing(false)}>Annuleren</button>
        {/* An empty name is a CLEARED name: the code becomes the label again, which is the honest
            state for a werf nobody has named. */}
        <span className="faint">Leeg laten = terug naar {p.projectCode}</span>
      </span>
      {error && <span className="faint" style={{ color: 'var(--danger)' }}>{error}</span>}
    </span>
  );
}

/** The project list. Only admitted AND assigned projects come back — the backend decides, not this. */
export function Projects({ onOpen }: { onOpen: (p: A.PlannerProject) => void }) {
  const q = useQuery({ queryKey: keys.projects, queryFn: A.listProjects });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <Banner kind="err">{(q.error as Error).message}</Banner>;
  if ((q.data ?? []).length === 0) {
    return <Banner kind="info">
      Je bent aan geen enkele werf toegewezen. Vraag een beheerder om je toe te voegen.
    </Banner>;
  }
  return (
    <div className="stack">
      {q.data!.map((p) => (
        <button key={p.projectId} className="card row" style={{ justifyContent: 'space-between', textAlign: 'left' }}
                onClick={() => onOpen(p)}>
          {/* THE NAME IS THE HEADLINE AND THE CODE IS THE FOOTNOTE. A werf is "Healthy Home" or
              "Langestraat" to everyone who works on it; "BS-644" is what the accounting system calls
              it. Both are here, in the order a human reads them — and the name is editable in place. */}
          <WerfName p={p} />
          <span className="faint">
            {p.phaseCount} fasen · {p.workpackageCount} werkpakketten
            {p.plannedStart && <> · {p.plannedStart} → {p.plannedEnd}</>}
          </span>
        </button>
      ))}
    </div>
  );
}

/**
 * The project plan: phases, workpackages, assignments, conflicts, capacity.
 *
 * EVERY VERDICT IS THE SERVER'S. Conflicts, feasibility and overload are rendered from
 * `/delivery/conflicts` and `/delivery/capacity` verbatim. Nothing here recomputes a rule — the same
 * discipline the repository layer follows, for the same reason.
 */
export function ProjectDetail({ project, onBack }: { project: A.PlannerProject; onBack: () => void }) {
  const id = project.projectId;
  const phases = useQuery({ queryKey: keys.phases(id), queryFn: () => A.getPhases(id) });
  const wps = useQuery({ queryKey: keys.workpackages(id), queryFn: () => A.getWorkpackages(id) });
  const conflicts = useQuery({ queryKey: keys.conflicts(id), queryFn: () => A.getConflicts(id) });
  const capacity = useQuery({ queryKey: keys.capacity(id), queryFn: () => A.getCapacity(id) });
  const assignments = useQuery({ queryKey: keys.assignments(id), queryFn: () => A.getAssignments(id) });

  const conflictsFor = (wpId: number) =>
    (conflicts.data?.conflicts ?? []).filter((c) => c.subjectType === 'workpackage' && c.subjectId === wpId);

  return (
    <div className="stack">
      <div className="row">
        <button className="btn small" onClick={onBack}>‹ Werven</button>
        <span className="stack" style={{ gap: 0, minWidth: 0 }}>
          <strong className="werf-name">{A.werfLabel(project).name}</strong>
          {A.werfLabel(project).code && <span className="werf-code">{A.werfLabel(project).code}</span>}
        </span>
        <span className="faint">{project.customerName ?? ''}</span>
        <span className="spacer" />
        {conflicts.data?.blocking && <span className="badge conflict">plan blokkeert</span>}
      </div>

      {conflicts.data && conflicts.data.conflicts.length > 0 && (
        <Banner kind={conflicts.data.blocking ? 'err' : 'warn'}>
          <strong>{conflicts.data.conflicts.length} bevinding(en) in het plan</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {conflicts.data.conflicts.slice(0, 6).map((c, i) => <li key={i}>{c.detail}</li>)}
          </ul>
        </Banner>
      )}
      {(capacity.data?.conflicts.length ?? 0) > 0 && (
        <Banner kind="warn">
          <strong>Capaciteit</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {capacity.data!.conflicts.slice(0, 6).map((c, i) => <li key={i}>{c.detail}</li>)}
          </ul>
        </Banner>
      )}

      {(phases.isPending || wps.isPending) ? <Spinner /> : (
        <div className="card">
          <table className="plan">
            <thead>
              <tr><th>Fase / werkpakket</th><th>Discipline</th><th>Planning</th><th>Toegewezen</th><th>Status</th></tr>
            </thead>
            <tbody>
              {(phases.data ?? []).map((ph) => (
                <>
                  <tr key={`ph-${ph.id}`} style={{ background: 'var(--surface-2)' }}>
                    <td colSpan={2}><strong>{ph.templateName ?? `Fase ${ph.id}`}</strong></td>
                    <td>{ph.plannedStart ? `${ph.plannedStart} → ${ph.plannedEnd}` : <span className="faint">niet gepland</span>}</td>
                    <td />
                    <td><span className="badge">{ph.status}</span></td>
                  </tr>
                  {(wps.data ?? []).filter((w) => w.projectPhaseId === ph.id).map((w) => {
                    const mine = (assignments.data ?? []).filter((a) => a.workpackageId === w.id && !a.supersededAt);
                    const cs = conflictsFor(w.id);
                    return (
                      <tr key={`wp-${w.id}`}>
                        <td style={{ paddingLeft: 24 }}>{w.title}</td>
                        <td className="faint">{w.disciplineCode ?? '—'}</td>
                        <td>{w.plannedStart ? `${w.plannedStart} → ${w.plannedEnd}` : <span className="faint">niet gepland</span>}</td>
                        <td>
                          <span className="row" style={{ gap: 4 }}>
                            {mine.map((a) => (
                              <span key={a.id} className="row" style={{ gap: 4 }}>
                                <Avatar id={String(a.resourceId)} name={a.resourceName} />
                                <span className="faint">{hours(a.allocatedHours * 60)}</span>
                              </span>
                            ))}
                            {mine.length === 0 && <span className="faint">niemand</span>}
                          </span>
                        </td>
                        <td>
                          <span className="badge">{w.lifecycleState}</span>
                          {cs.length > 0 && <span className="badge conflict" title={cs.map((c) => c.detail).join('; ')}> {cs.length}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="faint">
        Verplaatsen en toewijzen gebeurt in de kalender — daar zie je meteen de capaciteit van de
        betrokken mensen.
      </p>
    </div>
  );
}
